import { type Control } from "react-hook-form"

import { Card,CardContent,CardHeader,CardTitle } from "@/components/ui/card"
import { FormControl,FormField,FormItem,FormLabel,FormMessage } from "@/components/ui/form"
import {
Select,
SelectContent,
SelectItem,
SelectTrigger,
SelectValue,
} from "@/components/ui/select"
import { NumericInput } from "@/components/ui/numeric-input"
import type { UnitOfMeasurement } from "@/types"

import type { SaleConfigFormValues } from "./sale-config-utils"

interface SellMechanicsSectionProps {
  control: Control<SaleConfigFormValues>
  pricingMode: "simple" | "tiered"
  filteredSellUoms: UnitOfMeasurement[]
  allCategoryUoms: UnitOfMeasurement[]
  allWeightUoms: UnitOfMeasurement[]
  sellType: "WEIGHT" | "COUNT" | "VOLUME"
  onSellTypeChange: (newSellType: string) => void
}

export const SellMechanicsSection = ({
  control,
  pricingMode,
  filteredSellUoms,
  allCategoryUoms,
  allWeightUoms,
  sellType,
  onSellTypeChange,
}: SellMechanicsSectionProps) => {
  // In simple mode, show ALL category UOMs (legacy behavior)
  // In tiered mode, show only filtered UOMs based on scheme
  const sellUomOptions = pricingMode === 'simple' ? allCategoryUoms : filteredSellUoms
  if (pricingMode === "simple") {
    return (
      <Card>
        <CardHeader className="px-4 py-1.5">
          <CardTitle className="text-base">Sell Mechanics</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4 px-4 pb-4">
          <FormField
            control={control}
            name="sellUomId"
            render={({ field }) => (
              <FormItem className="md:col-span-2">
                <FormLabel className="gap-0.5">Sell Unit <span className="text-destructive">*</span></FormLabel>
                <Select
                  onValueChange={(val) => {
                    field.onChange(val)
                    const selectedUom = sellUomOptions.find((u) => u.id === val)
                    if (selectedUom) {
                      onSellTypeChange(selectedUom.measurementType)
                    }
                  }}
                  value={field.value || undefined}
                >
                  <FormControl>
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select Unit (e.g. Gram, Each, Pack)" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {sellUomOptions.map((u) => (
                      <SelectItem key={u.id} value={u.id}>
                        <span className="flex items-center gap-2">
                          <span>{u.name} ({u.shortForm})</span>
                          <span className="text-xs text-muted-foreground uppercase opacity-70">
                            — {u.measurementType === 'WEIGHT' ? 'Weight (Bulk)' : u.measurementType === 'VOLUME' ? 'Volume (Liquid)' : 'Count (Unit)'}
                          </span>
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          {sellType === "COUNT" && (
            <div className="md:col-span-1 animate-in fade-in slide-in-from-top-2 duration-300">
              <div className="space-y-2">
                <FormLabel>Each unit weighs</FormLabel>
                <div className="flex border rounded-md overflow-hidden focus-within:border-ring focus-within:ring-ring/50 focus-within:ring-[3px] transition-[color,box-shadow] bg-background">
                  <div className="flex-1 flex items-center relative">
                    <FormField
                      control={control}
                      name="unitWeight"
                      render={({ field }) => (
                        <NumericInput
                          value={field.value ?? ""}
                          onChange={(val) => {
                            field.onChange(val === "" ? undefined : val)
                          }}
                          allowDecimals={true}
                          step="any"
                          className="border-0 focus-within:ring-0 flex-1 h-10 rounded-none shadow-none"
                        />
                      )}
                    />
                  </div>
                  <div className="w-px bg-border shrink-0 h-10" />
                  <FormField
                    control={control}
                    name="unitWeightUomId"
                    render={({ field }) => (
                      <Select onValueChange={field.onChange} value={field.value || ""}>
                        <SelectTrigger className="flex-1 border-0 rounded-none h-10 focus:ring-0 shadow-none bg-transparent">
                          <SelectValue placeholder="Unit" />
                        </SelectTrigger>
                        <SelectContent>
                          {allWeightUoms.map((u) => (
                            <SelectItem key={u.id} value={u.id}>{u.name} ({u.shortForm})</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  />
                </div>
                <FormMessage className="text-xs" />
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    )
  }

  if (pricingMode === "tiered" && sellType === "COUNT") {
    return (
      <Card>
        <CardHeader className="px-4 py-1.5">
          <CardTitle className="text-base">Sell Mechanics</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4 px-4 pb-4">
          <div className="md:col-span-2">
            <div className="space-y-2">
              <label className="text-sm font-medium leading-none">Each unit weighs</label>
              <div className="flex border rounded-md overflow-hidden focus-within:border-ring focus-within:ring-ring/50 focus-within:ring-[3px] transition-[color,box-shadow] bg-background">
                <div className="flex-1 flex items-center relative">
                  <FormField
                    control={control}
                    name="unitWeight"
                    render={({ field }) => (
                      <NumericInput
                        value={field.value ?? ""}
                        onChange={(val) => {
                          field.onChange(val === "" ? undefined : val)
                        }}
                        allowDecimals={true}
                        step="any"
                        className="border-0 focus-within:ring-0 flex-1 h-10 rounded-none shadow-none"
                      />
                    )}
                  />
                </div>
                <div className="w-px bg-border shrink-0 h-10" />
                <FormField
                  control={control}
                  name="unitWeightUomId"
                  render={({ field }) => (
                    <Select onValueChange={field.onChange} value={field.value || ""}>
                      <SelectTrigger className="flex-1 border-0 rounded-none h-10 focus:ring-0 shadow-none bg-transparent">
                        <SelectValue placeholder="Unit" />
                      </SelectTrigger>
                      <SelectContent>
                        {allWeightUoms.map((u) => (
                          <SelectItem key={u.id} value={u.id}>{u.name} ({u.shortForm})</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                />
              </div>
              <FormMessage className="text-xs" />
            </div>
          </div>
        </CardContent>
      </Card>
    )
  }

  return null
}
